import { ReactNode, useEffect, useRef, useState } from 'react'
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm
} from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
  Textfield
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import { DataService } from 'api/DataService'
import {
  getActiveMerchantUuid,
  registerRecordMerchant
} from 'lib/merchant-group'
import {
  constructorAccountsPayable,
  costTypeMapper,
  frequencyTypeMapper
} from 'models/AccountsPayable'
import moment from 'moment-timezone'
import { useAccountsPayableContext } from 'templates/AccountsPayable/context'
import FrequencyControls from 'templates/Customers/Modals/BodyModalNewChargeOrRecurrence/Components/FrequencyControls'
import { v4 } from 'uuid'

import { DatePicker } from 'components/DatePicker'
import IconContainer from 'components/IconContainer/IconContainer'
import InputController from 'components/InputController'
import TagsPopover from 'components/TagsPopover'
import UploadComponent from 'components/UploadComponent'
import { FileParams } from 'components/UploadComponent/FileList'

import { MAX_RECEIPTS } from 'utils/attachment-limits'
import { currency, formatMoney, priceConverter } from 'utils/FormatMoney'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

import TypeTabs from './components/accountsPayableTypeTabs/TypeTabs'
import AllocationsSection from './components/AllocationsSection'
import PayeeAutocomplete from './components/autocompletes/PayeeAutocomplete'
import SourceAutocomplete from './components/autocompletes/SourceAutocomplete'
import TooltipWrapper from './components/TooltipWrapper'
import { itemUnitPrice } from './duplicate.utils'
import { NewExpenseSchema, schemaNewExpense } from './schema'

const frequencyMapper: {
  [key: number]: { frequency_type: number; frequency_number: number }
} = {
  0: {
    frequency_type: 2,
    frequency_number: 1
  },
  1: {
    frequency_type: 3,
    frequency_number: 1
  },
  2: {
    frequency_type: 3,
    frequency_number: 3
  },
  3: {
    frequency_type: 3,
    frequency_number: 6
  },
  4: {
    frequency_type: 4,
    frequency_number: 1
  }
}

const getFrequencyFromOutside = (
  frequencyType: string,
  number: number
): number | undefined => {
  const type =
    frequencyTypeMapper[frequencyType as keyof typeof frequencyTypeMapper]

  return Object.entries(frequencyMapper).find(
    ([, value]) =>
      value.frequency_type === type && value.frequency_number === number
  )?.[0] as unknown as number | 1
}

type ModalNewExpenseProps = {
  isOpen: boolean
  handleClose: () => void
  uuid?: string
  // `isRecurrence` diz a que registro o uuid se refere: a despesa criada ou,
  // quando a criação é recorrente, a recorrência que o back devolve no lugar.
  onSuccess?: (uuid: string, isRecurrence: boolean) => void
  defaultValues?: Partial<NewExpenseSchema>
  // SC-10999: seletor "Empresa" da criação na visão multi-empresas
  companySelector?: ReactNode
}

const ModalNewExpense = ({
  isOpen,
  handleClose,
  uuid,
  onSuccess,
  defaultValues,
  companySelector
}: ModalNewExpenseProps) => {
  const {
    setExpenseFormModal,
    setReloadTable,
    expenseFormModal,
    setExpenseDetailModal
  } = useAccountsPayableContext()

  const [files, setFiles] = useState<FileParams[]>([])
  // Lista dos anexos que a despesa já tinha, repassada ao UploadComponent para semear o
  // estado interno dele. `files` sozinho não serve: é estado externo, e o X só sabia
  // apagar no servidor o que estivesse no estado interno do componente.
  const [initialFiles, setInitialFiles] = useState<FileParams[]>([])
  const [hasExpenseCycle, setHasExpenseCycle] = useState<boolean>(false)

  const prevValues = {
    totalCyclesStored: [0, 0] as [number, number],
    costTypeStored: null as number | null
  }
  const [isPaid, setIsPaid] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)
  const [loadingFile, setLoadingFile] = useState(false)
  const [totalValue, setTotalValue] = useState<number>(0)
  const [renderCount, setRenderCount] = useState(0)
  const [tabValues, setTabValues] = useState<[number, number]>([12, 12])
  const [customTabValues, setCustomTabValues] = useState<
    [string | undefined, string | undefined]
  >([undefined, undefined])
  const [userModifiedTabs, setUserModifiedTabs] = useState<boolean[]>([
    false,
    false
  ])
  const [prevValue, setPrevValue] = useState(0)

  const [previousMarkAsPaidValue, setPreviousMarkAsPaidValue] =
    useState<boolean>()
  const isMobile = useMediaQuery('(max-width: 960px )')
  const [dialogOpen, setDialogOpen] = useState(false)
  const {
    control,
    watch,
    handleSubmit,
    setValue,
    getValues,
    reset,
    clearErrors,
    formState: { errors }
  } = useForm<NewExpenseSchema>({
    defaultValues: {
      ...(!uuid && {
        accountsPayableFrequency: 0
      }),
      costType: 1,
      totalCycles: 12,
      frequency: 1,
      enableRateio: false,
      allocations: [
        {
          uuid: v4(),
          category: { label: '', value: '' },
          costCenter: { label: '', value: '' },
          amount: undefined,
          percentage: undefined
        }
      ],
      repeat: false,
      markAsPaid: false,
      expenses: [
        {
          uuid: v4(),
          description: undefined,
          price: undefined,
          quantity: 1
        }
      ],
      tags: []
    },
    resolver: yupResolver(schemaNewExpense)
  })
  useEffect(() => {
    if (!uuid) {
      if (watch('accountsPayableFrequency') === 1 && watch('costType') === 2) {
        setValue('repeat', true)
      } else {
        setValue('repeat', false)
      }
    }
  }, [watch('accountsPayableFrequency'), watch('costType')])

  const {
    fields: expensesFields,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'expenses'
  })

  const {
    fields: allocationsFields,
    append: appendAllocation,
    remove: removeAllocation
  } = useFieldArray({
    control,
    name: 'allocations'
  })

  const [rateioDialogOpen, setRateioDialogOpen] = useState(false)

  const handleToggleRateio = (next: boolean) => {
    if (!next && (watch('allocations')?.length ?? 0) > 1) {
      setRateioDialogOpen(true)
      return
    }
    setValue('enableRateio', next)
  }

  const confirmDisableRateio = () => {
    const extraIndexes = allocationsFields
      .map((_, index) => index)
      .filter((index) => index > 0)
    if (extraIndexes.length) removeAllocation(extraIndexes)
    // Sem rateio, a linha que sobra cobre 100% da despesa. Setar o estado
    // explicito (em vez de undefined) evita que o user veja R$ 0,00 e tente
    // redigitar caso reative o rateio antes de salvar.
    setValue('allocations.0.amount', totalValue)
    setValue('allocations.0.percentage', 100)
    setValue('enableRateio', false)
    setRateioDialogOpen(false)
  }

  const uploadRef = useRef<UploadComponent>({} as UploadComponent)

  const handleClickToSubmit: SubmitHandler<NewExpenseSchema> = async () => {
    setDialogOpen(true)
  }

  const handleSubmitForm: SubmitHandler<NewExpenseSchema> = async (
    dataForm
  ) => {
    setLoading(true)

    // O await é obrigatório: sem ele o setLoading(false) abaixo rodava na hora, o
    // botão nunca travava e o duplo clique criava a despesa em dobro.
    try {
      await toast.promise(
        async () => {
          // Espelha o cálculo de total_cycles abaixo; recorrência = total_cycles != 1.
          const totalCyclesValue = watch('repeat')
            ? dataForm.totalCycles == 13
              ? dataForm.totalCyclesCustom
              : dataForm.totalCycles
            : 1
          const isRecurrence = totalCyclesValue != 1

          // O `amount` só pode ser omitido quando o registro gravado é uma RECORRÊNCIA
          // (que guarda apenas percentual; o valor de cada ciclo é calculado na geração).
          // Na CRIAÇÃO quem decide é o total_cycles, que o backend usa para escolher o
          // branch. Na EDIÇÃO quem decide é o endpoint: ao editar UM CICLO o destino é
          // /v1/accounts_payables/:uuid, e como o ciclo herda frequência + total_cycles do
          // pai, `isRecurrence` fica true e o amount ia embora como null. O backend então
          // reconstruía o valor a partir do percentual — que aqui é derivado do valor
          // digitado com toFixed(2) e portanto tem perda: R$ 3.333,33 volta como 33.33% e
          // regrava 3.333,00, com a soma ainda fechando e sem disparar validação nenhuma.
          const omitAllocationAmount = uuid
            ? !!expenseFormModal.isRecurrence
            : isRecurrence

          const allocationsAttributes = (() => {
            if (!dataForm.enableRateio) {
              const single = dataForm.allocations?.[0]
              if (!single) return []
              return [
                {
                  uuid: single.uuid,
                  accounts_payable_category_uuid: single.category.value,
                  accounts_payable_cost_center_uuid:
                    single.costCenter?.value || null,
                  // Sem rateio, a allocation única cobre 100% da despesa.
                  // Recorrência guarda só percentual; o amount é calculado por ciclo.
                  amount: omitAllocationAmount
                    ? null
                    : priceConverter(totalValue),
                  percentage: 100
                }
              ]
            }

            const rows = dataForm.allocations ?? []
            // Normaliza os percentuais pra somarem exatamente 100: a última linha
            // pega o resto. Sem isso, o arredondamento por linha pode somar
            // 99.99/100.01 e a validação da recorrência rejeita o rateio.
            const roundedBeforeLast = rows
              .slice(0, -1)
              .reduce(
                (acc, allocation) =>
                  acc + Number((allocation.percentage ?? 0).toFixed(2)),
                0
              )

            return rows.map((allocation, index) => ({
              uuid: allocation.uuid,
              accounts_payable_category_uuid: allocation.category.value,
              accounts_payable_cost_center_uuid:
                allocation.costCenter?.value || null,
              // Recorrência guarda só percentual; o amount é calculado por ciclo.
              amount: omitAllocationAmount
                ? null
                : allocation.amount != null
                ? priceConverter(allocation.amount)
                : null,
              percentage:
                index === rows.length - 1
                  ? Number((100 - roundedBeforeLast).toFixed(2))
                  : Number((allocation.percentage ?? 0).toFixed(2))
            }))
          })()

          const data = {
            amount_billed: priceConverter(totalValue),
            cost_type:
              watch('accountsPayableFrequency') === 1 ? 3 : dataForm.costType,
            competence_date: moment(dataForm.competence).format('YYYY-MM-DD'),
            due_date: moment(
              moment
                .tz(dataForm.dueDate, 'America/Sao_Paulo')
                .endOf('day')
                .toDate()
            ).format('YYYY-MM-DD'),
            accounts_payable_payee_uuid: dataForm.payee.value,
            accounts_payable_allocations_attributes: allocationsAttributes,
            accounts_payable_items_attributes: dataForm.expenses?.map(
              (expense, index) => ({
                description: expense.description,
                amount_billed: priceConverter(watch(`expenses.${index}.total`)),
                amount_unit: priceConverter(watch(`expenses.${index}.price`)),
                quantity: expense.quantity,
                ...(expense.uuid ? { uuid: expense.uuid } : {})
              })
            ),
            ...(dataForm.source?.value
              ? { accounts_payable_source_uuid: dataForm.source.value }
              : uuid && !expenseFormModal.isRecurrence
              ? // Origem limpa ao editar: null explícito desfaz o vínculo. Omitir a
                // chave o back lê como "manter" (SC-10620).
                { accounts_payable_source_uuid: null }
              : {}),
            ...(dataForm.markAsPaid &&
            !dataForm.repeat &&
            !(
              watch('costType') === 2 && watch('accountsPayableFrequency') === 1
            )
              ? {
                  date_paid: moment(dataForm.datePaid).format('YYYY-MM-DD'),
                  amount_paid: priceConverter(watch('amountPaid') || 0)
                }
              : uuid && isPaid && !expenseFormModal.isRecurrence
              ? // Desligou "Marcar como pago" numa despesa paga: null desfaz a baixa.
                // Omitir as chaves o back lê como "manter" (SC-10620). Só manda quando
                // estava paga, porque esse caminho do back também limpa a origem.
                { date_paid: null, amount_paid: null }
              : {}),
            total_cycles: watch('repeat')
              ? dataForm.totalCycles == 13
                ? dataForm.totalCyclesCustom
                : dataForm.totalCycles
              : 1,
            // total_cycles:
            //   (!dataForm.repeat &&
            //     watch('costType') === 1 &&
            //     watch('accountsPayableFrequency') === 1) ||
            //   hasExpenseCycle ||
            //   !dataForm.repeat
            //     ? 1
            //     : dataForm.totalCycles == 13
            //     ? dataForm.totalCyclesCustom
            //     : dataForm.totalCycles,

            ...(dataForm.repeat ||
            (watch('costType') === 2 && watch('accountsPayableFrequency') === 1)
              ? {
                  ...frequencyMapper[dataForm.frequency as number]
                }
              : {}),
            ...(dataForm.documentUuids?.length &&
            !dataForm.repeat &&
            !(
              watch('costType') === 2 && watch('accountsPayableFrequency') === 1
            ) &&
            files.length > 0
              ? { document_uuids: dataForm.documentUuids }
              : {}),
            description: dataForm.description,
            tags: dataForm.tags?.map((tag: any) => tag.id).join(';') || ''
          }

          const response = await DataService({
            data: data,
            type: uuid ? 'PATCH' : 'POST',
            url: uuid
              ? expenseFormModal.isRecurrence
                ? `/v1/accounts_payable_recurrences/${uuid}`
                : `/v1/accounts_payables/${uuid}`
              : '/v1/accounts_payables'
          })
          // Em erro não vem `record`: ler o uuid antes dessa checagem estourava
          // um TypeError e o toast mostrava ele no lugar da mensagem do back.
          if (response.error) {
            setDialogOpen(false)
            throw new Error(
              response.error && (response.detail_error || response.message)
            )
          } else {
            const createdUuid = response.data.record.uuid
            // Com recorrência o `record` que volta é a recorrência, não a despesa
            // avulsa — quem consome `onSuccess` precisa saber para qual dos dois
            // registros o uuid aponta.
            const createdIsRecurrence =
              !!watch('repeat') ||
              (watch('costType') === 2 &&
                watch('accountsPayableFrequency') === 1)
            // SC-10999: a conta nova é da empresa do formulário; o detalhe
            // aberto em seguida precisa saber disso
            registerRecordMerchant(createdUuid, getActiveMerchantUuid())
            setExpenseDetailModal({
              isOpen: true,
              uuid: createdUuid,
              isRecurrence: createdIsRecurrence
            })
            setReloadTable((prev) => !prev)
            setExpenseFormModal({
              isOpen: false,
              uuid: '',
              isRecurrence: false
            })
            setDialogOpen(false)

            // Chamar callback onSuccess se fornecido
            if (onSuccess) {
              onSuccess(createdUuid, createdIsRecurrence)
            }

            return response.data.message
          }
        },
        {
          pending: `${uuid ? 'Atualizando' : 'Criando'} despesa`,
          success: {
            render({ data }) {
              return `${data}`
            }
          },
          error: {
            render({ data }) {
              return `${
                (data as any)?.message ||
                'Houve um erro ao criar a conta a pagar.'
              }`
            }
          }
        }
      )
    } catch {
      // erro já exibido pelo toast.promise
    }

    setLoading(false)
  }

  const expensesWatch = watch(
    expensesFields
      .map((_, index) => [
        `expenses.${index}.price` as const,
        `expenses.${index}.quantity` as const
      ])
      .flat()
  )
  useEffect(() => {
    const total = expensesFields.reduce((sum, _, index) => {
      const price = priceConverter(watch(`expenses.${index}.price`) || 0)
      const quantity = Number(watch(`expenses.${index}.quantity`)) || 0
      const product = price * quantity

      if (watch(`expenses.${index}.total`) !== product) {
        setValue(`expenses.${index}.total`, product)
      }
      return sum + product
    }, 0)

    setTotalValue(total)
  }, [expensesWatch, expensesFields])

  // Mantém amount e percentual sincronizados quando o total muda.
  // Em recorrência o amount NÃO é persistido (fica null no banco — a validação
  // do model só exige amount em AccountsPayable, não no template). O percentual
  // é a fonte da verdade, então derivamos o amount exibido a partir dele. Sem
  // isso, ao editar uma recorrência o amount vinha 0 (null) e este effect zerava
  // o percentual que veio do banco. Nos demais casos o amount é a fonte e o
  // percentual é derivado.
  useEffect(() => {
    if (!getValues('enableRateio')) return
    // No load, este effect dispara junto com o de `totalValue` (mesmo commit),
    // então `totalValue` ainda pode ser 0 aqui. Recalcular com total 0 zera o
    // percentual -> o eco do NumberFormatCustom zera o amount -> e como o
    // percentual passa a ser derivado de amount=0, trava em 0/0. Pular enquanto
    // o total não está pronto preserva os valores que vieram do banco.
    if (totalValue <= 0) return
    const isRecurrenceMode =
      !!watch('repeat') ||
      (watch('costType') === 2 && watch('accountsPayableFrequency') === 1) ||
      !!expenseFormModal.isRecurrence
    const currentAllocations = getValues('allocations') ?? []
    currentAllocations.forEach((allocation, index) => {
      if (isRecurrenceMode) {
        const percentage = Number(allocation?.percentage ?? 0)
        setValue(
          `allocations.${index}.amount`,
          totalValue > 0
            ? Number(((totalValue * percentage) / 100).toFixed(2))
            : 0
        )
      } else {
        const amount = priceConverter((allocation?.amount ?? 0) as number)
        setValue(
          `allocations.${index}.percentage`,
          totalValue > 0 ? Number(((amount / totalValue) * 100).toFixed(2)) : 0
        )
      }
    })
  }, [totalValue, allocationsFields.length])

  useEffect(() => {
    if (isPaid) return
    setValue('datePaid', watch('dueDate'))
  }, [watch('dueDate')])

  useEffect(() => {
    if (isPaid) return
    setValue('amountPaid', priceConverter(totalValue))
  }, [totalValue])

  useEffect(() => {
    if (watch('repeat')) {
      setPreviousMarkAsPaidValue(watch('markAsPaid'))
      setValue('markAsPaid', false)
    } else {
      setValue('markAsPaid', previousMarkAsPaidValue || false)
    }
  }, [watch('repeat')])

  useEffect(() => {
    if (files) {
      setValue('documentUuids', files.map((file) => file.id).filter(Boolean))
      clearErrors('documentUuids')
    }
  }, [files])

  useEffect(() => {
    if (!uuid || !expenseFormModal.isRecurrence) return
    if (watch('costType') === 1 && watch('accountsPayableFrequency') === 1) {
      setPrevValue(1)
      setValue('costType', 2)
    }
    if (watch('accountsPayableFrequency') === 0) {
      if (prevValue === 1) {
        setValue('costType', prevValue)
        setPrevValue(0)
      }
    }
  }, [watch('accountsPayableFrequency')])

  useEffect(() => {
    if (watch('costType') === 2) {
      if (watch('accountsPayableFrequency') === 1) {
        setValue('markAsPaid', false)
      }
    }
    if (watch('repeat')) {
      setValue('markAsPaid', false)
    }
  }, [watch('costType'), watch('accountsPayableFrequency'), watch('repeat')])

  useEffect(() => {
    const currentTab = Number(watch('accountsPayableFrequency'))
    if (renderCount <= 1 && uuid) {
      setRenderCount((prev) => prev + 1)
      return
    }

    setValue('totalCycles', undefined)
    setValue('totalCyclesCustom', '')

    if (!uuid) {
      if (tabValues[currentTab] === 13 && customTabValues[currentTab]) {
        setValue('totalCycles', 13)
        setValue('totalCyclesCustom', customTabValues[currentTab])
      } else {
        setValue('totalCycles', tabValues[currentTab])
      }
      return
    }

    if (userModifiedTabs[currentTab]) {
      if (tabValues[currentTab] === 13 && customTabValues[currentTab]) {
        setValue('totalCycles', 13)
        setValue('totalCyclesCustom', customTabValues[currentTab])
      } else {
        setValue('totalCycles', tabValues[currentTab])
        setValue('totalCyclesCustom', '')
      }
    } else {
      const storedValue = prevValues.totalCyclesStored[currentTab]
      const isCustom = storedValue >= 13

      if (isCustom) {
        setValue('totalCycles', 13)
        setValue('totalCyclesCustom', String(storedValue))

        setCustomTabValues((prev) => {
          const newValues = [...prev] as [
            string | undefined,
            string | undefined
          ]
          newValues[currentTab] = String(storedValue)
          return newValues
        })
      } else {
        setValue('totalCycles', storedValue > 0 ? storedValue : 12)
        setValue('totalCyclesCustom', '')
      }
    }
  }, [watch('accountsPayableFrequency')])

  useEffect(() => {
    if (loading) return

    const currentTab = Number(watch('accountsPayableFrequency'))
    const currentCycles = watch('totalCycles')
    const currentCustomCycles = watch('totalCyclesCustom')

    if (currentCycles !== undefined) {
      setUserModifiedTabs((prev) => {
        const newModified = [...prev]
        newModified[currentTab] = true
        return newModified
      })

      setTabValues((prev) => {
        const newValues = [...prev] as [number, number]
        newValues[currentTab] = currentCycles
        return newValues
      })

      if (currentCycles === 13 && currentCustomCycles) {
        setCustomTabValues((prev) => {
          const newValues = [...prev] as [
            string | undefined,
            string | undefined
          ]
          newValues[currentTab] = currentCustomCycles
          return newValues
        })
      } else {
        setCustomTabValues((prev) => {
          const newValues = [...prev] as [
            string | undefined,
            string | undefined
          ]
          newValues[currentTab] = undefined
          return newValues
        })
      }
    }
  }, [watch('totalCycles'), watch('totalCyclesCustom')])

  useEffect(() => {
    if (!uuid) return

    const getExpense = async () => {
      setLoading(true)
      const response = await DataService({
        type: 'GET',
        url: expenseFormModal.isRecurrence
          ? `v1/accounts_payables_recurrences/${uuid}`
          : `/v1/accounts_payables/${uuid}`
      })

      setHasExpenseCycle(!!response?.data?.record?.recurrence_cycle)
      const accountPayable = constructorAccountsPayable(response.data.record)

      const totalCyclesValue =
        accountPayable.totalCycles === 0
          ? 0
          : accountPayable.totalCycles > 1
          ? accountPayable.totalCycles >= 13
            ? 13
            : accountPayable.totalCycles
          : 12
      setIsPaid(accountPayable.status === 'paid')
      reset({
        amountPaid: accountPayable.amountPaid,
        enableRateio: (accountPayable.allocations?.length ?? 0) > 1,
        allocations:
          accountPayable.allocations && accountPayable.allocations.length > 0
            ? accountPayable.allocations.map((allocation) => ({
                uuid: v4(),
                category: {
                  value: allocation.category.uuid,
                  label: allocation.category.name
                },
                costCenter: {
                  value: allocation.costCenter.uuid,
                  label: allocation.costCenter.name
                },
                amount: allocation.amount,
                percentage: allocation.percentage
              }))
            : [
                {
                  uuid: v4(),
                  category: { label: '', value: '' },
                  costCenter: { label: '', value: '' },
                  amount: undefined,
                  percentage: undefined
                }
              ],
        // Guard: moment.tz(undefined) retorna AGORA. Sem isso, uma competência ausente
        // vira hoje e é enviada no submit, corrompendo a recorrência.
        competence: accountPayable.competenceDate
          ? moment
              .tz(accountPayable.competenceDate, 'America/Sao_Paulo')
              .endOf('day')
              .toDate()
          : undefined,
        costType:
          (costTypeMapper as any)[accountPayable.costType] === 3
            ? accountPayable.totalCycles > 1
              ? 2
              : 1
            : (costTypeMapper as any)[accountPayable.costType],
        accountsPayableFrequency:
          (costTypeMapper as any)[accountPayable.costType] === 3 ? 1 : 0,
        datePaid: accountPayable.datePaid
          ? moment
              .tz(accountPayable.datePaid, 'America/Sao_Paulo')
              .endOf('day')
              .toDate()
          : undefined,
        description: accountPayable.description || '',
        // Guard: moment.tz(undefined) retorna AGORA. Ver competence acima.
        dueDate: accountPayable.dueDate
          ? moment
              .tz(accountPayable.dueDate, 'America/Sao_Paulo')
              .endOf('day')
              .toDate()
          : undefined,
        expenses: accountPayable?.items?.map((item) => ({
          description: item?.description,
          price: itemUnitPrice(item),
          quantity: item?.quantity,
          total: item?.amountBilled,
          uuid: item?.uuid
        })),

        frequency:
          accountPayable.frequencyNumber && accountPayable.frequencyType
            ? getFrequencyFromOutside(
                accountPayable.frequencyType,
                accountPayable.frequencyNumber
              )
            : 1,
        markAsPaid: !!accountPayable.datePaid,
        // Despesa sem beneficiário derrubava o reset inteiro num TypeError, e como
        // getExpense é async o erro virava rejeição não tratada: o modal abria vazio, sem
        // nem chegar no setFiles logo abaixo — ou seja, sem a lista de anexos. Acontece em
        // despesa criada por API ou importação de planilha, onde o payee é opcional.
        payee: accountPayable.payee
          ? {
              label: accountPayable.payee.name,
              value: accountPayable.payee.uuid
            }
          : { label: '', value: '' },
        repeat:
          !!accountPayable.frequencyNumber &&
          !!accountPayable.frequencyType &&
          !accountPayable.datePaid &&
          accountPayable.totalCycles !== 1,
        source: accountPayable.source
          ? {
              label: accountPayable.source.name,
              value: accountPayable.source.uuid
            }
          : undefined,
        totalCycles: totalCyclesValue,
        totalCyclesCustom:
          accountPayable.totalCycles >= 13
            ? (accountPayable.totalCycles as unknown as string)
            : undefined,
        documentUuids: accountPayable.documentFiles
          .map((document) => document.id)
          .filter((id): id is string => !!id),
        tags: accountPayable.tags || []
      })

      setPreviousMarkAsPaidValue(!!accountPayable.datePaid)

      const filesData = accountPayable.documentFiles.map((document) => ({
        id: document.id || '',
        name: document.name || '',
        readableSize: document.readableSize || 0,
        preview: document.preview || '',
        uploaded: true,
        url: document.url || '',
        error: false,
        progress: 100,
        file: new File([], document.name || '')
      }))

      setFiles(filesData)
      setInitialFiles(filesData)

      setLoading(false)
    }

    getExpense()
  }, [])

  useEffect(() => {
    if (uuid || !defaultValues) return
    reset({
      accountsPayableFrequency: 0,
      costType: 1,
      totalCycles: 12,
      frequency: 1,
      enableRateio: false,
      allocations: [
        {
          uuid: v4(),
          category: { label: '', value: '' },
          costCenter: { label: '', value: '' },
          amount: undefined,
          percentage: undefined
        }
      ],
      repeat: false,
      markAsPaid: false,
      expenses: [
        {
          uuid: v4(),
          description: undefined,
          price: undefined,
          quantity: 1
        }
      ],
      tags: [],
      ...defaultValues
    })
  }, [])

  useEffect(() => {
    const dueDate = watch('dueDate')
    if (!dueDate || uuid) return
    // Competência que veio de fora junto com o vencimento (conciliação da fatura do
    // cartão: competência na data da compra, vencimento na fatura; duplicar despesa) fica
    // como veio. Só quando o usuário troca o vencimento a competência volta a segui-lo.
    const keepsDefault =
      !!defaultValues?.competence &&
      !!defaultValues?.dueDate &&
      new Date(dueDate).toDateString() ===
        new Date(defaultValues.dueDate).toDateString()
    if (keepsDefault) return
    setValue('competence', dueDate)
  }, [watch('dueDate')])
  return (
    <Modal
      handleClose={handleClose}
      open={isOpen}
      width="100%"
      maxWidth="85vw"
      maxHeight="90vh"
      height="auto"
    >
      <ModalBody>
        <ModalHeader onClose={handleClose}>
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer icon="paid" />
            <Typography variant="smBold">
              {uuid ? 'Editar ' : 'Nova '}
              {expenseFormModal.isRecurrence ? 'Recorrência' : 'Despesa'}
            </Typography>
          </Stack>
        </ModalHeader>

        <ModalContent>
          {companySelector}
          <Stack gap="15px">
            <Box
              sx={{
                display: 'grid',
                gap: '15px',
                gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }
              }}
            >
              <Controller
                control={control}
                name="payee"
                render={({
                  field: { onChange, value },
                  fieldState: { error }
                }) => {
                  const errorTyped = error as any
                  return (
                    <PayeeAutocomplete
                      key={value?.value || 'payee'}
                      value={value}
                      onChange={(
                        _: any,
                        value: { label: string; value: string }
                      ) => {
                        const option = value as { label: string; value: string }
                        onChange(option ?? { value: '', label: '' })
                      }}
                      errorMessage={errorTyped?.label?.message}
                      setValue={setValue}
                    />
                  )
                }}
              />

              <Controller
                control={control}
                name="source"
                render={({
                  field: { onChange, value },
                  fieldState: { error }
                }) => {
                  const errorTyped = error as any
                  return (
                    <SourceAutocomplete
                      key={value?.value || 'source'}
                      value={
                        value as {
                          label: string
                          value: string
                        }
                      }
                      onChange={(
                        _: any,
                        value: { label: string; value: string }
                      ) => {
                        const option = value as {
                          label: string
                          value: string
                        }
                        onChange(option ?? { value: '', label: '' })
                      }}
                      errorMessage={errorTyped?.label?.message}
                      setValue={setValue}
                    />
                  )
                }}
              />
            </Box>

            <TooltipWrapper disabled={hasExpenseCycle}>
              <Box sx={{ opacity: hasExpenseCycle ? 0.5 : 1 }}>
                <TypeTabs disabled={hasExpenseCycle} control={control} />
                <Box
                  sx={{
                    boxShadow: '0px 2px 10px 0px rgba(0, 0, 0, 0.10)',
                    borderRadius: '6px',
                    my: 2
                  }}
                >
                  <Box>
                    <Box p={2}>
                      <Controller
                        name={'costType'}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <RadioGroup
                            aria-labelledby="group-type"
                            value={value}
                            onChange={(event) => {
                              const newValue = Number(event.target.value)
                              onChange(newValue)
                            }}
                            row
                            sx={{ gap: 2 }}
                          >
                            <FormControlLabel
                              value={1}
                              disabled={
                                hasExpenseCycle ||
                                (!!expenseFormModal.isRecurrence &&
                                  watch('accountsPayableFrequency') === 1)
                              }
                              control={<Radio />}
                              label={
                                <Stack
                                  alignItems={isMobile ? 'end' : 'center'}
                                  gap="5px"
                                  direction="row"
                                >
                                  <Typography
                                    sx={{
                                      ...((hasExpenseCycle ||
                                        (!!expenseFormModal.isRecurrence &&
                                          watch('accountsPayableFrequency') ===
                                            1)) && {
                                        color: theme.palette.neutral[400]
                                      })
                                    }}
                                    variant={
                                      Number(watch('costType')) === 1
                                        ? hasExpenseCycle
                                          ? 'smLight'
                                          : 'smBold'
                                        : 'smLight'
                                    }
                                  >
                                    {Number(
                                      watch('accountsPayableFrequency')
                                    ) === 1
                                      ? 'À vista'
                                      : 'Fixa'}
                                  </Typography>
                                  <Typography
                                    variant="smLight"
                                    color={theme.palette.neutral[400]}
                                    fontSize={isMobile ? '12px' : '14px'}
                                  >
                                    {Number(
                                      watch('accountsPayableFrequency')
                                    ) === 1
                                      ? '(pago em uma vez)'
                                      : '(mesmo valor todos os meses)'}
                                  </Typography>
                                </Stack>
                              }
                            />
                            <FormControlLabel
                              value={2}
                              disabled={hasExpenseCycle}
                              control={<Radio />}
                              label={
                                <Stack
                                  alignItems="center"
                                  gap="5px"
                                  direction="row"
                                >
                                  <Typography
                                    sx={{
                                      ...(hasExpenseCycle && {
                                        color: theme.palette.neutral[400]
                                      })
                                    }}
                                    variant={
                                      Number(watch('costType')) === 2
                                        ? hasExpenseCycle
                                          ? 'smLight'
                                          : 'smBold'
                                        : 'smLight'
                                    }
                                  >
                                    {Number(watch('accountsPayableFrequency'))
                                      ? 'Parcelado'
                                      : 'Variável'}
                                  </Typography>
                                  <Typography
                                    variant="smLight"
                                    color={theme.palette.neutral[400]}
                                    fontSize={isMobile ? '12px' : '14px'}
                                  >
                                    {Number(
                                      watch('accountsPayableFrequency')
                                    ) === 1
                                      ? '(pago em diversas vezes)'
                                      : '(valor muda todos os meses)'}
                                  </Typography>
                                </Stack>
                              }
                            />
                          </RadioGroup>
                        )}
                      />
                    </Box>
                    <Divider />
                    {!(
                      watch('accountsPayableFrequency') === 1 &&
                      watch('costType') === 1
                    ) && (
                      <Box p={2} pt={0}>
                        <Controller
                          control={control}
                          name={'repeat'}
                          render={({ field }) => (
                            <>
                              <Accordion
                                expanded={field.value}
                                onChange={(_, expanded) =>
                                  setValue('repeat', expanded)
                                }
                                sx={{
                                  border: '1px solid transparent',
                                  borderRadius: '8px !important',
                                  boxShadow: 'none',
                                  '&:before': { display: 'none' },
                                  '&.Mui-expanded': {
                                    margin: 0,
                                    backgroundColor: '#fff'
                                  }
                                }}
                              >
                                <Stack gap="15px" mt="15px">
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Stack gap="2px">
                                      <Typography
                                        sx={{
                                          ...(hasExpenseCycle && {
                                            color: theme.palette.neutral[400]
                                          })
                                        }}
                                        variant="smBold"
                                      >
                                        Repetir
                                      </Typography>
                                      <Typography
                                        variant="smLight"
                                        color={theme.palette.neutral[400]}
                                      >
                                        Lançar essa despesa nos próximos meses
                                      </Typography>
                                    </Stack>
                                    {watch('accountsPayableFrequency') !== 1 &&
                                      !expenseFormModal.isRecurrence && (
                                        <Switch
                                          key="switch"
                                          disabled={hasExpenseCycle}
                                          checked={field.value}
                                          {...field}
                                        />
                                      )}
                                  </Stack>

                                  {(watch('repeat') ||
                                    watch('accountsPayableFrequency') ===
                                      1) && (
                                    <Box>
                                      <FrequencyControls<NewExpenseSchema>
                                        disabled={hasExpenseCycle}
                                        control={control}
                                        watch={watch}
                                        {...(Number(
                                          watch('accountsPayableFrequency')
                                        ) === 1 &&
                                          Number(watch('costType')) === 2 && {
                                            hiddenValues: [0]
                                          })}
                                      />
                                    </Box>
                                  )}
                                </Stack>
                              </Accordion>
                            </>
                          )}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </TooltipWrapper>

            <Box
              sx={{
                display: 'grid',
                gap: '15px',
                gridTemplateColumns: !isMobile ? '1fr 1fr' : '1fr'
              }}
            >
              <Controller
                control={control}
                name="dueDate"
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error }
                }) => {
                  return (
                    <DatePicker
                      inputRef={ref}
                      helperText={error?.message}
                      error={!!error?.message}
                      label={`Data de vencimento${
                        watch('repeat') ? ' da primeira despesa' : ''
                      } *`}
                      minDate={
                        new Date(
                          new Date().setFullYear(new Date().getFullYear() - 2)
                        )
                      }
                      maxDate={
                        new Date(
                          new Date().setFullYear(new Date().getFullYear() + 2)
                        )
                      }
                      onChange={onChange}
                      renderInput={(props: any) => (
                        <Textfield {...props} name="dueDate" />
                      )}
                      toolbarPlaceholder="Escolha a data de vencimento"
                      value={value ? value : null}
                    />
                  )
                }}
              />

              <Controller
                control={control}
                name="competence"
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error }
                }) => {
                  return (
                    <DatePicker
                      inputRef={ref}
                      helperText={error?.message}
                      error={!!error?.message}
                      label={`Competência${
                        watch('repeat') ? ' da primeira despesa' : ''
                      }`}
                      views={['year', 'month']}
                      openTo="month"
                      inputFormat="MM/yyyy"
                      key="competence"
                      minDate={
                        new Date(
                          new Date().setFullYear(new Date().getFullYear() - 2)
                        )
                      }
                      maxDate={
                        new Date(
                          new Date().setFullYear(new Date().getFullYear() + 2)
                        )
                      }
                      onChange={onChange}
                      renderInput={(props: any) => (
                        <Textfield {...props} name="competence" />
                      )}
                      toolbarPlaceholder="Escolha a competência"
                      value={value || null}
                    />
                  )
                }}
              />
            </Box>

            <Stack gap="15px" mb="10px">
              {expensesFields.map((field, index) => (
                <Box
                  key={field.uuid}
                  sx={{
                    display: 'grid',
                    gap: '10px',
                    gridTemplateColumns: !isMobile ? '6.5fr 5.5fr' : '1fr'
                  }}
                >
                  <InputController
                    control={control}
                    name={`expenses.${index}.description`}
                  >
                    <Textfield label="Item *" placeholder="Descrição" />
                  </InputController>

                  <Box
                    sx={{
                      display: 'grid',
                      gap: '10px',
                      gridTemplateColumns: '2fr 1fr 2fr 0.5fr'
                    }}
                  >
                    <Controller
                      control={control}
                      name={`expenses.${index}.price`}
                      render={({
                        field: { onChange, value, ref },
                        fieldState: { error }
                      }) => (
                        <Textfield
                          customStartAdornment={
                            <Typography mr="6px" variant="smLight">
                              R$
                            </Typography>
                          }
                          inputRef={ref}
                          helperText={error?.message}
                          error={!!error?.message}
                          label={
                            watch('costType') === 2 &&
                            watch('accountsPayableFrequency') === 1 ? (
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap="3px"
                              >
                                Valor
                                <Typography color={theme.palette.neutral[400]}>
                                  (parcela)
                                </Typography>
                                *
                              </Stack>
                            ) : (
                              'Valor *'
                            )
                          }
                          mask="money"
                          name="price"
                          placeholder="Digite o valor"
                          startAdornment={true}
                          onChange={onChange}
                          value={currency(value || '0')}
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name={`expenses.${index}.quantity`}
                      render={({
                        field: { onChange, value },
                        fieldState: { error }
                      }) => (
                        <Textfield
                          label="Qtde. *"
                          type="number"
                          placeholder="Digite a quantidade"
                          helperText={error?.message}
                          error={!!error?.message}
                          onChange={(e) => {
                            onChange(e)
                          }}
                          value={value}
                        />
                      )}
                    />

                    <Typography mt={3} sx={{ alignSelf: 'center' }}>
                      {formatMoney(watch(`expenses.${index}.total`))}
                    </Typography>

                    <Box
                      sx={{
                        visibility:
                          index == 0 && expensesFields.length === 1
                            ? 'hidden'
                            : 'visible',
                        display: 'grid',
                        placeItems: 'center',
                        pt: 3
                      }}
                    >
                      <IconButton onClick={() => remove(index)}>
                        <Icon icon="delete" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}

              <Box
                sx={{ height: '1px', bgcolor: theme.palette.neutral[700] }}
              ></Box>

              <Button
                onClick={() =>
                  append({
                    description: '',
                    price: 0,
                    total: 0,
                    quantity: 1,
                    uuid: v4()
                  })
                }
                variantButton="ghost"
                sx={{ alignSelf: 'end', width: 'fit-content', my: '-5px' }}
              >
                <Stack direction="row" gap="5px" alignItems="center">
                  <Icon
                    icon="add"
                    sx={{ color: theme.palette.neutral[400], fontSize: '18px' }}
                  />
                  <Typography
                    sx={{
                      color: theme.palette.neutral[400],
                      fontWeight: '600',
                      fontSize: '14px'
                    }}
                  >
                    Adicionar item
                  </Typography>
                </Stack>
              </Button>

              <Stack
                alignItems="end"
                bgcolor={theme.palette.neutral[900]}
                p="12px 22px"
              >
                <Stack direction="row" gap="50px">
                  <Typography variant="smBold">TOTAL:</Typography>
                  <Typography
                    variant="smBold"
                    color={theme.palette.neutral[400]}
                  >
                    R$ {currency(totalValue)}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <AllocationsSection
              control={control}
              watch={watch}
              setValue={setValue}
              errors={errors}
              allocationsFields={allocationsFields}
              appendAllocation={appendAllocation}
              removeAllocation={removeAllocation}
              totalValue={totalValue}
              onToggleRateio={handleToggleRateio}
            />

            <Stack gap="22px">
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <Textarea
                    fullWidth={true}
                    placeholder="Adicione uma descrição à sua despesa"
                    label="Descrição"
                    maxLength={2000}
                    style={{
                      height: '75px',
                      overflow: 'auto',
                      resize: 'vertical',
                      maxHeight: '50vh'
                    }}
                    {...field}
                  />
                )}
              />

              {!expenseFormModal.isRecurrence && (
                <Controller
                  control={control}
                  name={'markAsPaid'}
                  render={({ field }) => (
                    <>
                      <Accordion
                        sx={{
                          border: '1px solid #E8EBE8',
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                          borderRadius: '8px !important',
                          backgroundColor: '#FAFBFA',
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                          '&.Mui-expanded': {
                            margin: 0,
                            backgroundColor: '#fff'
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#FAFBFA !important'
                          }
                        }}
                        expanded={field.value}
                        disabled={
                          hasExpenseCycle
                            ? false
                            : watch('costType') === 1 &&
                              watch('accountsPayableFrequency') === 1
                            ? false
                            : watch('costType') === 2 &&
                              watch('accountsPayableFrequency') === 1
                            ? true
                            : watch('repeat')
                        }
                        onChange={(_, expanded) =>
                          setValue('markAsPaid', expanded)
                        }
                      >
                        <AccordionSummary
                          hasSwitch
                          expandIcon={
                            <Switch checked={field.value} {...field} />
                          }
                        >
                          <Typography variant="smBold">
                            Marcar como pago
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2 }}>
                          <Box
                            sx={{
                              display: 'grid',
                              gap: '15px',
                              gridTemplateColumns: !isMobile ? '1fr 1fr' : '1fr'
                            }}
                          >
                            <Controller
                              control={control}
                              name="datePaid"
                              render={({
                                field: { onChange, value, ref },
                                fieldState: { error }
                              }) => {
                                return (
                                  <DatePicker
                                    inputRef={ref}
                                    helperText={error?.message}
                                    error={!!error?.message}
                                    label="Data de pagamento *"
                                    minDate={
                                      new Date(
                                        new Date().setFullYear(
                                          new Date().getFullYear() - 2
                                        )
                                      )
                                    }
                                    maxDate={
                                      new Date(
                                        new Date().setFullYear(
                                          new Date().getFullYear() + 2
                                        )
                                      )
                                    }
                                    onChange={onChange}
                                    renderInput={(props: any) => (
                                      <Textfield {...props} name="datePaid" />
                                    )}
                                    toolbarPlaceholder="Escolha a data de pagamento"
                                    value={value ? value : null}
                                  />
                                )
                              }}
                            />

                            <Controller
                              control={control}
                              name="amountPaid"
                              render={({ field, fieldState: { error } }) => (
                                <Textfield
                                  customStartAdornment={
                                    <Typography mr="6px" variant="smLight">
                                      R$
                                    </Typography>
                                  }
                                  helperText={error?.message}
                                  error={!!error?.message}
                                  label="Valor pago *"
                                  mask="money"
                                  placeholder="Preencha o valor"
                                  startAdornment={true}
                                  {...field}
                                  value={currency(field.value || '0')}
                                />
                              )}
                            />
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </>
                  )}
                />
              )}

              <Stack sx={{ mt: -1.5 }}>
                <UploadComponent
                  docType="account_payable"
                  type="accounts_payables"
                  disabled={
                    hasExpenseCycle
                      ? false
                      : watch('costType') === 1 &&
                        watch('accountsPayableFrequency') === 1
                      ? false
                      : watch('costType') === 2 &&
                        watch('accountsPayableFrequency') === 1
                      ? true
                      : watch('repeat')
                  }
                  title={
                    !isMobile ? (
                      <Typography
                        sx={{
                          display: 'flex',
                          gap: '6px',
                          alignItems: 'center'
                        }}
                        variant="smBold"
                      >
                        Anexar um arquivo |{' '}
                        <Typography variant="smLight">
                          Exemplo: comprovante, fatura
                        </Typography>
                      </Typography>
                    ) : (
                      'Anexar comprovante'
                    )
                  }
                  disabledText="Você não pode adicionar arquivos em despesas recorrentes ou parceladas"
                  subTitle={
                    !isMobile
                      ? 'Clique aqui ou arraste os arquivos para anexá-los'
                      : 'Clique aqui para inserir o comprovante'
                  }
                  accept={{
                    'application/pdf': ['.pdf'],
                    'application/vnd.oasis.opendocument.text': ['.odt'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                      ['.docx'],
                    'image/jpeg': ['.jpeg', '.jpg'],
                    'image/png': ['.png'],
                    'image/webp': ['.webp']
                  }}
                  maxFiles={MAX_RECEIPTS}
                  disableList
                  ref={uploadRef}
                  initialFiles={initialFiles}
                  setStateExternal={setFiles}
                  setLoadingExternal={setLoadingFile}
                />
                {errors.documentUuids && (
                  <Typography
                    ml={'5px'}
                    mt={'-8px'}
                    mb={'8px'}
                    color="#990017"
                    variant="xsBold"
                  >
                    {errors?.documentUuids.message}
                  </Typography>
                )}
                {files.length && !watch('repeat') ? (
                  <Box display="flex" flexDirection="column" mt={-0.5} mb={1}>
                    {loadingFile ? (
                      <CircularProgress size={'24px'} />
                    ) : (
                      files.map((file) => (
                        <Box
                          key={file.id}
                          display={'flex'}
                          justifyContent={'space-between'}
                        >
                          <Typography
                            ml={'5px'}
                            mt="4px"
                            color={theme.palette.neutral[500]}
                            variant="xsLight"
                          >
                            {file.name}
                          </Typography>
                          <Tooltip
                            arrow={true}
                            placement="top"
                            title={'Excluir arquivo'}
                          >
                            <Box>
                              <Icon
                                sx={{
                                  cursor: 'pointer',
                                  width: '18px',
                                  height: '18px'
                                }}
                                onClick={() =>
                                  uploadRef.current?.handleDelete(file.id)
                                }
                                icon="cancel"
                                color={'disabled'}
                              />
                            </Box>
                          </Tooltip>
                        </Box>
                      ))
                    )}
                  </Box>
                ) : (
                  <></>
                )}
              </Stack>
            </Stack>
          </Stack>

          <Dialog
            icon={uuid ? 'edit' : 'paid'}
            title="Despesa"
            iconColor={theme.palette.primary.main}
            description={
              uuid
                ? `Confirma atualizar essa Despesa?`
                : 'Confirma criar essa despesa?'
            }
            setIsOpenDialog={setDialogOpen}
            isOpenDialog={dialogOpen}
            cancelButton
          >
            <Button
              autoFocus
              disabled={loading}
              loading={loading}
              onClick={handleSubmit(handleSubmitForm, toastFirstError)}
              fullWidth
            >
              {uuid ? 'Atualizar' : 'Criar'}
            </Button>
          </Dialog>

          <Dialog
            icon="error"
            title="Desabilitar rateio?"
            iconColor={theme.palette.primary.main}
            description="Isso vai remover as outras linhas de rateio e manter apenas a primeira. Deseja continuar?"
            setIsOpenDialog={setRateioDialogOpen}
            isOpenDialog={rateioDialogOpen}
            cancelButton
          >
            <Button autoFocus onClick={confirmDisableRateio} fullWidth>
              Continuar
            </Button>
          </Dialog>
        </ModalContent>

        <ModalFooter>
          <Stack direction="row" alignItems="center" gap={2}>
            {/* Tags Popover */}
            <TagsPopover
              control={control}
              name="tags"
              maxTags={10}
              saveMode="deferred"
            />

            <Button
              variantButton="primary"
              disabled={loading || loadingFile}
              onClick={handleSubmit(handleClickToSubmit, toastFirstError)}
            >
              Salvar
            </Button>
          </Stack>
        </ModalFooter>
      </ModalBody>
    </Modal>
  )
}

export default ModalNewExpense
